// THE FIRST PAINT WAITS FOR THE LANGUAGE, BUT NOT FOREVER.
//
// main.tsx renders once the active language's dictionary has loaded, so a
// Russian floor phone never flashes English. Every language but English and
// Russian is a separate download (i18n.ts), and on a poor connection that
// download can hang without ever failing. Waiting on it alone would leave the
// shell blank for as long as it hangs. After the budget the page renders in
// English, which is bundled; the user can pick their language again once the
// connection recovers.

/** How long the first paint waits for a lazily loaded dictionary. */
export const FIRST_PAINT_BUDGET_MS = 4_000;

/**
 * Resolves when `ready` settles or the budget runs out, whichever is first.
 * When the budget runs out, `fallback` runs (switch to the bundled English)
 * before it resolves. Returns which of the two happened.
 */
export async function raceFirstPaint(
  ready: Promise<unknown>,
  fallback: () => Promise<unknown>,
  budgetMs: number = FIRST_PAINT_BUDGET_MS,
): Promise<"ready" | "late"> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<"late">((resolve) => {
    timer = setTimeout(() => {
      resolve("late");
    }, budgetMs);
  });
  const settled = ready.then(
    () => "ready" as const,
    () => "ready" as const,
  );
  const outcome = await Promise.race([settled, late]);
  if (timer !== undefined) clearTimeout(timer);
  if (outcome === "late") {
    try {
      await fallback();
    } catch {
      /* English is bundled; a failed switch still renders with it as the fallback */
    }
  }
  return outcome;
}
